import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactModal from 'react-modal';

function FamilySettingsEditClan() {
  const { isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const loggedFamilyAdmin = localStorage.getItem('family_admin');
  const [clanModalOpen, setClanModalOpen] = useState(false);
  const [selectedClanId, setSelectedClanId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false); // NEW

  // Fetch family_data + clans in one request (backend should return both)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['clanSettings'],
    queryFn: () =>
      axios.get(`${API_URL}/clans-settings-api`, { withCredentials: true })
        .then(res => res.data),
    staleTime: 0,
    gcTime: Infinity,
  });

  const familyData = data?.family_data || {};
  const clanList = data?.clans || [];

  useEffect(() => {
    if (familyData?.clan_id) {
      setSelectedClanId(familyData.clan_id);
    }
  }, [familyData]);

  const openModal = () => {
    setSelectedClanId(familyData?.clan_id || (clanList[0]?.id ?? null));
    setClanModalOpen(true);
  };

  const handleUpdateClan = () => {
    if (!selectedClanId || isNaN(selectedClanId)) {
      toast.error('Invalid clan selected.', { position: 'top-right', autoClose: 3000 });
      return;
    }

    setIsUpdating(true); // disable button
    axios.put(`${API_URL}/update-clan-settings-api`, { clan_id: selectedClanId }, { withCredentials: true })
      .then(() => {
        toast.success('Clan updated successfully', { position: 'top-right', autoClose: 3000 });
        setClanModalOpen(false);
        queryClient.invalidateQueries(['clanSettings']);
        navigate('/family-settings');
      })
      .catch(err => {
        console.error('Update failed:', err.response?.data || err.message);
        toast.error('Update failed. Please try again.', { position: 'top-right', autoClose: 3000 });
      })
      .finally(() => {
        setIsUpdating(false); // re-enable button
      });
  };

  if (isLoading) {
    return (
      <div className='loadingspinnerDiv'>
        <div className='loadingspinner'></div>
      </div>
    );
  }

  if (isError) return <p>Error loading clan data.</p>;

  const currentClanName = clanList.find(c => c.id === familyData?.clan_id)?.clan_name || 'Not Set';

  return (
    <div className='scrollPageContainer theme-light'>
      <div className='main-wrapper'>
        {isSidebarVisible && <Sidebar />}
        <Header toggleSidebar={toggleSidebar} />
        <Sidebar show={showSidebar} toggle={toggleSidebar} />
        <Footerbar toggleSidebar={toggleSidebar} />
        <ToastContainer />

        <div className='page-wrapper'>
          <div className='content container-fluid'>
            
            <div className='row'>
              <div className='col-3 col-md-3'>
                <Link to='/family-settings' style={{ textDecoration: 'none', color: '#184A2C' }}>
                  <i className='fa fa-arrow-left' style={{ fontSize: '18px' }}></i>
                </Link>
              </div>
            </div>

            <div className='row'>
              <div className='col-12 col-md-12'>
                <div className='card info-card-mock'>
                  <div className='card-body'>
                    <p style={{ fontWeight: '600', textAlign: 'center' }}>Edit Clan</p>
                    <div className='mt-4 border rounded p-3 d-flex align-items-center'>
                      <p className='mb-0'>
                        <strong>Clan:</strong> {currentClanName}
                      </p>
                      {loggedFamilyAdmin === '1' && (
                        <button
                          onClick={openModal}
                          className='btn btn-link p-0'
                          style={{ color: '#184A2C', marginLeft: '2%' }}
                        >
                          <i className='fas fa-edit' style={{ fontSize: '16px' }}></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <ReactModal isOpen={clanModalOpen} onRequestClose={() => setClanModalOpen(false)}
                className='Modal' overlayClassName='Overlay'>
                <div className='modal-header modal-header-green'>
                  <h5 className='modal-title'>Edit Clan</h5>
                  <button type='button' className='btn-close modalCloseButton' onClick={() => setClanModalOpen(false)}></button>
                </div>
                <div className='modal-body'>

                <div
                  className="mb-4 rounded"
                  style={{
                    border: '1px solid rgba(117,180,55,.4)',
                    background:
                      'radial-gradient(circle at 20% 20%, rgba(117,180,55,.08) 0%, rgba(24,74,44,.02) 80%)',
                    boxShadow: '0 6px 20px rgba(0,0,0,.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '260px',
                  }}
                >

                  <label className="small fw-semibold text-dark">Clan Name</label>

                  <div
                    className="p-2 flex-grow-1"
                    style={{
                      overflowY: 'auto',
                      scrollbarWidth: 'thin',
                    }}
                  >
                  <select
                      className="form-select"
                      value={selectedClanId || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "insert_clan") {
                          navigate("/family-settings-edit-clan-add");
                        } else {
                          setSelectedClanId(parseInt(value));
                        }
                      }}
                    >
                      <option>Select Clan</option>
                      {clanList.map((clan) => (
                        <option key={clan.id} value={clan.id}>
                          {clan.clan_name}
                        </option>
                      ))}

                      <option value="insert_clan" style={{ fontWeight: "900" }}>
                        <b>Not in list </b> / I'll add my own
                      </option>
                    </select>

                    </div>

                 </div>         
                </div>
                <div className='modal-footer'>
                  <button
                    className='btn btn-success'
                    onClick={handleUpdateClan}
                    disabled={isUpdating} // disable during update
                  >
                    {isUpdating ? 'Updating...' : 'Update Clan'}
                  </button>
                </div>
              </ReactModal>



            </div>
          </div>
        </div>
      </div>


      <style>{`
:root{
  --brand: #75B437;
  --brand-600: #5fa12f;
  --brand-700: #4d8e29;
  --brand-dark: #184A2C;
  --ink: #000000;
  --bg: #EBF4ED;
  --bg-soft: #f4faf6;
}

.scrollPageContainer { min-height: 100vh; overflow-y: auto; }
 

.bg-light-subtle{
  background: linear-gradient(135deg, #f8fafc, #f1f5f9) !important;
  border:2px dashed #cbd5e1 !important;
}

.btn-success{
  background: linear-gradient(
    135deg,
    var(--brand) 0%,
    var(--brand-dark) 100%
  );
  border:none;
  color:#fff;
  box-shadow:0 4px 12px rgba(24,74,44,.35);
  transition: all .3s ease;
}
.btn-success:hover{
  background: linear-gradient(
    135deg,
    var(--brand-dark) 0%,
    #0f3320 100%
  );
  transform: translateY(-1px);
  box-shadow:0 8px 20px rgba(24,74,44,.45);
}

.content.container-fluid{ padding-left:1rem; padding-right:1rem; }
@media (min-width:768px){
  .content.container-fluid{ padding-left:1.5rem; padding-right:1.5rem; }
}

.btn-icon {
  --btn-bg:#f1f5f2;
  --btn-bg-hover:#e7efe9;
  --btn-border:#dfe9e2;
  background:var(--btn-bg);
  border:1px solid var(--btn-border);
  border-radius:10px;
  padding:6px 10px;
  line-height:1;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  transition: background .15s ease, border-color .15s ease, transform .06s ease;
}
.btn-icon i { font-size:.9rem; }
.btn-icon:hover { background:var(--btn-bg-hover); transform: translateY(-1px); }

.chip{
  width:28px;
  height:28px;
  border-radius:9999px;
  color:#fff;
  display:inline-flex;
  align-items:center;
  justify-content:center;
}
.chip-emerald{
  background:linear-gradient(135deg,var(--brand),var(--brand-dark));
}
.chip-emerald i { color:#fff; }

/* modal header tone now matches brand */
.modal-header-green{
  background: radial-gradient(circle at 20% 20%,
    rgba(117,180,55,.08) 0%,
    rgba(24,74,44,.02) 80%);
  border-bottom:1px solid rgba(117,180,55,.25);
  padding:1rem;
}

@media (max-width:575px){
  .history-title{ font-size:1rem; }
  .history-text{ font-size:.9rem; line-height:1.4; }
  .small{ font-size:.8rem; }
  .modal-body{ padding:.75rem !important; }
  .modal-footer{ padding:.75rem 1rem; gap:.5rem; }
  .modal-footer .btn{ flex:1; }
  .form-control-sm{ padding:.45rem .7rem; font-size:.9rem; }
}

.page-header-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 2rem 2rem 1.5rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  margin-bottom: 2rem;
  border: 1px solid #e5e7eb;
}

.header-content-mock {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}
.mock-h1 {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--brand-dark);
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.header-icon-mock {
  width: 48px;
  height: 48px;
  background: linear-gradient(
    135deg,
    var(--brand) 0%,
    var(--brand-dark) 100%
  );
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  box-shadow:0 4px 12px rgba(24,74,44,.3);
}
.mock-sub {
  color: #6b7280;
  font-size: 0.95rem;
  margin: 0;
}

.btn-add-new-mock,
.btn-add-story {
  background: linear-gradient(
    135deg,
    var(--brand) 0%,
    var(--brand-dark) 100%
  );
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(24,74,44,.35);
  cursor: pointer;
}
.btn-add-new-mock:hover,
.btn-add-story:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(24,74,44,.45);
  background: linear-gradient(
    135deg,
    var(--brand-dark) 0%,
    #0f3320 100%
  );
}

@media (max-width:768px){
  .header-content-mock {
    flex-direction: column;
    align-items: stretch;
  }
  .btn-add-new-mock {
    width: 100%;
    justify-content: center;
  }
}

/* ======================= INFO GRID (Family Name / Tribe / Clan) ======================= */

.info-grid-mock {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.info-card-mock {
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.5s ease-out;
}
.info-card-mock::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(
    90deg,
    var(--brand) 0%,
    var(--brand-dark) 100%
  );
}
.info-card-mock:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
  border-color: rgba(117,180,55,.35);
}

.card-header-section-mock {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.25rem;
}

.card-icon-mock {
  width: 40px;
  height: 40px;
  background: radial-gradient(circle at 30% 30%,
    rgba(117,180,55,.18) 0%,
    rgba(24,74,44,.08) 70%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--brand-dark);
  font-size: 1.1rem;
  flex-shrink: 0;
  border:2px solid rgba(117,180,55,.4);
}
.card-actions-mock {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.btn-edit-mock {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  cursor: pointer;
  font-size: 0.85rem;
  background: #f3f4f6;
  color: #6b7280;
}
.btn-edit-mock:hover {
  background: var(--bg-soft);
  color: var(--brand-dark);
  border-color: var(--brand);
  box-shadow: 0 4px 12px rgba(24,74,44,.2);
  transform: rotate(12deg) scale(1.1);
}

.card-title-mock {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  margin-bottom: 0.5rem;
}
.card-value-mock {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  word-wrap: break-word;
  line-height: 1.4;
}

.card-badge-mock {
  display: inline-block;
  background: radial-gradient(circle at 30% 30%,
    rgba(117,180,55,.18) 0%,
    rgba(24,74,44,.08) 70%);
  color: var(--brand-dark);
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-top: 0.5rem;
  border:1px solid rgba(117,180,55,.4);
}

/* Clan long description under clan card */
.clan-desc-mock {
  margin-top: 0.75rem;
  margin-bottom: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #4b5563;
  background: var(--bg-soft);
  border: 1px solid rgba(117,180,55,.25);
  border-radius: 8px;
  padding: 0.75rem .75rem;
  word-break: break-word;
  white-space: pre-wrap;
}

/* Section divider label "Totem" */
.section-divider-mock {
  display: flex;
  align-items: center;
  margin: 0.5rem 0 0 rem;
  gap: 1rem;
}
.divider-line-mock {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, transparent, #d1d5db, transparent);
}
.divider-text-mock {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 0.5rem 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

/* ======================= TOTEM FEATURE CARD ======================= */

.feature-card-mock {
  background: #ffffff;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.5s ease-out;
}
.feature-card-mock::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 5px;
  background: linear-gradient(
    90deg,
    var(--brand) 0%,
    var(--brand-dark) 100%
  );
}
.feature-header-mock {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f3f4f6;
}

.totem-content-mock {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 2rem;
  align-items: start;
}
.totem-image-section-mock { position: relative; }

.totem-image-container-mock {
  width: 280px;
  height: 280px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  border: 3px solid rgba(117,180,55,.4);
  transition: all 0.4s ease;
  position: relative;
  background:#f9fafb;
  display:flex;
  align-items:center;
  justify-content:center;
}
.totem-image-container-mock:hover {
  transform: scale(1.05) rotate(2deg);
  box-shadow: 0 12px 48px rgba(24,74,44,.28);
  border-color: var(--brand);
}
.totem-image-mock {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.totem-image-fallback {
  font-size: .9rem;
  color:#6b7280;
  text-align:center;
}
.totem-image-fallback .fallback-text{
  background:rgba(255,255,255,0.6);
  border-radius:8px;
  padding:.5rem .75rem;
  box-shadow:0 2px 8px rgba(0,0,0,.07);
}
.totem-badge-mock {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  color: var(--brand-dark);
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  font-size: 0.9rem;
  display:flex;
  align-items:center;
  gap:.5rem;
}

.totem-details-mock {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.totem-name-mock {
  font-size: 2rem;
  font-weight: 800;
  color: var(--brand-dark);
  margin: 0;
  line-height: 1.2;
}
.totem-description-mock {
  color: #4b5563;
  line-height: 1.7;
  font-size: 1rem;
  margin: 0;
}

.totem-attributes-mock {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}
.attribute-item-mock {
  background: radial-gradient(circle at 20% 20%,
    rgba(117,180,55,.08) 0%,
    rgba(24,74,44,.02) 80%);
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid rgba(117,180,55,.4);
}
.attribute-label-mock {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--brand-dark);
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}
.attribute-value-mock {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--brand-dark);
  word-break: break-word;
}

@media (max-width: 968px) {
  .totem-content-mock {
    grid-template-columns: 1fr;
  }
  .totem-image-container-mock {
    width: 100%;
    max-width: 280px;
    margin: 0 auto;
  }
  .totem-name-mock { font-size:1.6rem; }
}

/* full-width guideline layout */
.guideline-full {
  display: grid !important;
  grid-template-columns: auto 1fr auto; /* icon | text | actions */
  column-gap: 12px;
  padding: 12px 14px !important;
  border-radius: 14px !important;
  background: #f8f9fa !important;
  border: 1px solid #e9ecef !important;
}

/* big readable text */
.guideline-full .guideline-text {
  font-size: 1.05rem !important;
  line-height: 1.75 !important;
  margin: 0 !important;
  color: #333 !important;
}

/* tighten buttons */
.guideline-full .guideline-actions button {
  padding: 4px 8px !important;
  font-size: 12px !important;
}

/* Ensure card fills width on mobile */
@media (max-width: 576px) {
  .guideline-full {
    grid-template-columns: auto 1fr;  /* icon | text */
    grid-template-rows: auto auto;    /* buttons go below */
  }

  .guideline-full .guideline-actions {
    grid-column: 2;
    margin-top: 8px;
  }
}
/* =========================================================
   FULL-WIDTH GUIDELINE CARD (ICON | TEXT | BUTTONS)
   ========================================================= */
.guideline-full {
  display: grid !important;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  column-gap: 8px;

  background: #f8f9fa !important;
  border: 1px solid #e9ecef !important;
  border-radius: 14px !important;

  padding: 8px 10px !important;   /* <<< TIGHTER PADDING */
  width: 100% !important;
  transition: all 0.25s ease;
}
.card-body.guideline-container {
  padding-left: 0 !important;
  padding-right: 0 !important;
}


/* Hover lift */
.guideline-full:hover {
  background: #ffffff !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  transform: translateX(4px);
}

/* =========================================================
   TEXT — FULL WIDTH + LARGER + READABLE
   ========================================================= */
.guideline-full .guideline-text {
  font-size: 1.1rem !important;
  line-height: 1.75 !important;
  font-weight: 500 !important;
  margin: 0 !important;
  padding: 0 !important;
  color: #333 !important;
  width: 100% !important;
  display: block !important;
}

/* =========================================================
   BUTTONS — SMALL & CLEAN
   ========================================================= */

/* Space between guideline items */
.guideline-full + .guideline-full {
  margin-top: 12px;
}

/* =========================================================
   MOBILE LAYOUT — BUTTONS DROP BELOW TEXT
   ========================================================= */
@media (max-width: 576px) {
  .guideline-full {
    grid-template-columns: auto 1fr;   /* icon | text */
    grid-template-rows: auto auto;     /* buttons on second row */
  }

  .guideline-full .guideline-actions {
    grid-column: 2; 
    margin-top: 8px;
    justify-content: flex-start !important;
  }

  .guideline-full .guideline-text {
    font-size: 1.15rem !important;
    line-height: 1.85 !important;
  }
}


`}</style>


    </div>
  );
}

export default FamilySettingsEditClan;
